import React, { createContext, useContext } from "react";

import { PolymorphicPropsWithoutRef } from "@oneleet/utils/src/polymorphism";

import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

const DEFAULT_SIZE = "md";

const drawerSize = cva(
  "pointer-events-auto w-screen transform transition duration-500 ease-in-out sm:duration-700",
  {
    variants: {
      size: {
        xs: "max-w-xs",
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
      },
    },
    defaultVariants: {
      size: DEFAULT_SIZE,
    },
  },
);

type DrawerSizeVariants = VariantProps<typeof drawerSize>;

export type DrawerProps = {
  children?: React.ReactNode;
  className?: string;
  onClose?: () => void;
} & DrawerSizeVariants;

const slideVariants = {
  hidden: { x: "100%" },
  visible: { x: "0%" },
  exit: { x: "100%" },
};

const overlayVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 0.75 },
  exit: { opacity: 0 },
};

const DrawerContext = createContext<(() => void) | undefined>(undefined);

const Drawer = <T extends React.ElementType>({
  as,
  size = DEFAULT_SIZE,
  children,
  className,
  onClose = () => null,
  ...rest
}: PolymorphicPropsWithoutRef<DrawerProps, T>) => {
  const Component: React.ElementType = as ?? "div";

  return (
    <DrawerContext.Provider value={onClose}>
      <Dialog
        as={Component}
        className="relative z-[60]"
        open={true}
        onClose={onClose}
        {...rest}
      >
        <motion.div
          className="fixed inset-0 bg-gray-500"
          aria-hidden="true"
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={overlayVariants}
          transition={{ duration: 0.2, ease: "easeInOut" }}
        />
        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16"
              initial="hidden"
              animate="visible"
              exit="exit"
              variants={slideVariants}
              transition={{ duration: 0.2, ease: "easeInOut" }}
            >
              <Dialog.Panel
                className={twMerge(drawerSize({ size, className }))}
              >
                <div className="flex h-full flex-col overflow-y-auto bg-white shadow-xl">
                  {children}
                </div>
              </Dialog.Panel>
            </motion.div>
          </div>
        </div>
      </Dialog>
    </DrawerContext.Provider>
  );
};

function DrawerTitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Title
      as="h3"
      className={twMerge(
        "text-lg font-semibold leading-6 text-gray-900",
        className,
      )}
    >
      {children}
    </Dialog.Title>
  );
}

function DrawerCloseButton({ className }: { className?: string }) {
  const onClose = useContext(DrawerContext);

  if (!onClose) {
    throw new Error("DrawerCloseButton must be used within a Drawer");
  }

  return (
    <button
      type="button"
      onClick={onClose}
      className={twMerge(
        "relative text-gray-400 hover:text-gray-500",
        className,
      )}
    >
      <span className="absolute -inset-2.5" />
      <span className="sr-only">Close panel</span>
      <XMarkIcon aria-hidden="true" className="h-6 w-6" />
    </button>
  );
}

Drawer.Title = DrawerTitle;
Drawer.CloseButton = DrawerCloseButton;

export default Drawer;
